/**
 * Mantenimiento Pagos Ventas Generales — migración web de ares9640.
 *
 * Lista los pagos de ventas generales por rango de fechas, reimprime el
 * recibo (con el join correcto por factura — el del escritorio unía por
 * noprest y traía datos equivocados) y anula pagos: pagosgps.vpagcap = 0 y
 * reversa de gps.cobrado. A diferencia del escritorio, un pago anulado no
 * puede volver a anularse (evita el cobrado negativo). Anular requiere el
 * permiso `borrar` de ares9640.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { imprimirReciboVentaGeneralPorNumero } from "modules/ventasgenerales/pagosventasgenerales";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin pagos en el rango
      </Typography>
    </Stack>
  );
}

export default function MantenimientoPagosVentas({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/ventageneral_pagos_listar", {
        fechadesde: fecha1,
        fechahasta: fecha2,
      });
      if (ok) setRows((data.pagos || []).map((r) => ({ ...r, id: r.norecibo })));
      else showAlert({ message: data.Error || "No se pudieron listar los pagos", color: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const reimprimir = async (row) => {
    const res = await imprimirReciboVentaGeneralPorNumero(post, row.norecibo);
    if (!res.ok) showAlert({ message: res.error, color: "error" });
  };

  // ---- Anulación ----
  const [target, setTarget] = useState(null);
  const [anulando, setAnulando] = useState(false);

  const anular = async () => {
    setAnulando(true);
    try {
      const { ok, data } = await post("/api/ventageneral_pago_anular", {
        norecibo: target.norecibo,
      });
      if (ok) {
        showAlert({ message: data.ok || "Pago anulado", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo anular", color: "error" });
      }
    } finally {
      setAnulando(false);
    }
  };

  const columns = [
    { field: "norecibo", headerName: "Recibo#", width: 90 },
    { field: "nfactura", headerName: "Fact#", width: 80 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "tipogasto", headerName: "Categoría", width: 125 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 170 },
    { field: "cedula", headerName: "Cédula", width: 135 },
    { field: "banco", headerName: "Forma", width: 110 },
    {
      field: "vpagcap",
      headerName: "Valor",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "anulado",
      headerName: "Estado",
      width: 105,
      renderCell: (params) =>
        params.row.anulado ? (
          <Chip
            size="small"
            label="Anulado"
            sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Vigente"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setTarget(params.row)}
            disabled={params.row.anulado}
            sx={btnOutline(FP.color.danger)}
          >
            Anular
          </Button>
          <Button
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => reimprimir(params.row)}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Mantenimiento Pagos Ventas Generales"
          subtitle="Reimprime o anula pagos — la anulación devuelve el saldo a la venta"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} pagos
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmación de anulación */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>Anular pago</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Recibo <b>#{target?.norecibo}</b> de <b>{target?.nombres}</b> por RD${" "}
            <b>{fmtMoney(target?.vpagcap)}</b>. El pago quedará anulado y el saldo volverá a la
            factura <b>#{target?.nfactura}</b>.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              anulando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={anular}
            disabled={anulando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, anular
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
